// useAuth.js — хук авторизации с серверными сессиями
import { useState, useCallback } from 'react';
import { loginUser, registerUser, getUserRole } from '../api';

export function useAuth() {
    const [isLoggedIn, setIsLoggedIn] = useState(() => {
        const saved = localStorage.getItem('currentUser');
        if (!saved) return false;
        try {
            const user = JSON.parse(saved);
            return !!(user.login && user.token);
        } catch (e) {
            return false;
        }
    });

    const [currentUser, setCurrentUser] = useState(() => {
        const saved = localStorage.getItem('currentUser');
        if (!saved) return null;
        try {
            const user = JSON.parse(saved);
            return user.login && user.token ? user : null;
        } catch (e) {
            return null;
        }
    });

    const [authError, setAuthError] = useState('');
    const [authLoading, setAuthLoading] = useState(false);

    const login = useCallback(async (login, password) => {
        if (!login || !password) {
            setAuthError('Введите логин и пароль');
            return false;
        }
        setAuthLoading(true);
        setAuthError('');
        try {
            const data = await loginUser(login, password);
            if (data.success) {
                const user = { login, name: data.user.name, token: data.token };
                // Сохраняем остатки запросов (раздельные счётчики)
                if (typeof data.speechkitRemaining !== 'undefined') {
                    user.speechkitRemaining = data.speechkitRemaining;
                }
                if (typeof data.deepseekRemaining !== 'undefined') {
                    user.deepseekRemaining = data.deepseekRemaining;
                }
                // Сохраняем tier
                if (data.tier) user.tier = data.tier;
                if (data.tierName) user.tierName = data.tierName;
                if (data.tierExpiresAt) user.tierExpiresAt = data.tierExpiresAt;
                // Загружаем роль
                try {
                    const roleData = await getUserRole(login, data.token);
                    if (roleData.success) {
                        user.role = roleData.role || 'user';
                        user.mentorId = roleData.mentorId || null;
                    }
                } catch (e) { /* игнорируем */ }
                localStorage.setItem('currentUser', JSON.stringify(user));
                setCurrentUser(user);
                setIsLoggedIn(true);
                return true;
            }
            setAuthError(data.error || 'Ошибка входа');
            return false;
        } catch (err) {
            setAuthError('Не удалось связаться с сервером');
            return false;
        } finally {
            setAuthLoading(false);
        }
    }, []);

    const register = useCallback(async (login, password, inviteCode) => {
        if (!login || !password) {
            setAuthError('Заполните все поля');
            return false;
        }
        if (password.length < 4) {
            setAuthError('Пароль должен быть не менее 4 символов');
            return false;
        }
        setAuthLoading(true);
        setAuthError('');
        try {
            const data = await registerUser(login, password, login, inviteCode);
            if (data.success) {
                const user = { login, name: login, token: data.token, role: 'user', mentorId: null };
                localStorage.setItem('currentUser', JSON.stringify(user));
                setCurrentUser(user);
                setIsLoggedIn(true);
                return true;
            }
            setAuthError(data.error || 'Ошибка регистрации');
            return false;
        } catch (err) {
            setAuthError('Не удалось связаться с сервером');
            return false;
        } finally {
            setAuthLoading(false);
        }
    }, []);

    const logout = useCallback(() => {
        // Полная очистка
        localStorage.removeItem('currentUser');
        localStorage.removeItem('userProfile');
        setCurrentUser(null);
        setIsLoggedIn(false);
        setAuthError('');
    }, []);

    const getAuth = useCallback(() => {
        if (!currentUser) return null;
        return {
            login: currentUser.login,
            token: currentUser.token,
            role: currentUser.role || 'user',
            name: currentUser.name,
            mentorId: currentUser.mentorId || null
        };
    }, [currentUser]);

    return { isLoggedIn, currentUser, authError, authLoading, login, register, logout, getAuth, setAuthError };
}
